import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, of, Observable, Subject } from 'rxjs';
import {
  CoreUiPartialState,
  ApiService,
  getOrderTypes,
  getBranch,
  getDefaultCustomer,
  getActiveOrderVehicle,
  setSelectedVehicle,
  MessageService,
  clearSearchData,
  getUserRole,
  getNonCorpOrderTypes,
  getCashDrawer,
  getBrandDefaults,
  CustomerPrivacyPopupService,
  hasUnsavedChanges,
} from '@pos-app/core-ui';
import { select, Store } from '@ngrx/store';
import { switchMap, takeUntil, map, take, filter, debounceTime, finalize, distinctUntilChanged } from 'rxjs/operators';
import {
  Customer,
  OrderType,
  CustomerVehicle,
  Branch,
  CUSTOMER_TYPE,
  USER_ROLE,
  NonCorpStore,
  ON_BEHALF,
  DefaultCustomer,
  ORDER_TYPE,
  CustomerDetails,
  State,
  Vehicle,
  ShipTos,
} from '@pos-app/data';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormBuilder, Validators, ValidatorFn, ValidationErrors, UntypedFormControl, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OrdersPartialState } from '../../+state/orders.reducer';
import { CreateOrder, LoadParkedOrdersList } from '../../+state/orders.actions';
import { OrderCustomerVehicle, CustomerBillTo, CustomerShipTo } from '../../models/orders-start.model';
import { ActivatedRoute } from '@angular/router';
import { formatDate, NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { CustomersPartialState } from '../../../customers/+state/customers.reducer';
import { LoadLookupLists, LoadCustomerVehicles, SelectCountry, saveGenericTypeCustomerOrder } from '../../../customers/+state/customers.actions';
import { customersQuery } from '../../../customers/+state/customers.selectors';
import { ToastrService } from 'ngx-toastr';
import { CustomersSearchComponent } from '@pos-app/core-ui/lib/components/customer-search/customer-search.component';
import { OOESessionService } from '@pos-app/core-ui/lib/services/ooe-session.service';
import { IOOEUserState } from '../../../epc/models/userState.model';
import { getCombinedBranches } from '@pos-app/core-ui/lib/utils/utils-orders-combine-branches';
import { CustomersShipToSearchComponent } from '@pos-app/core-ui/lib/components/customer-ship-to-search/customer-ship-to-search.component';
import { CoreUiModule } from '../../../../../../../../libs/core-ui/src/lib/core-ui.module';
import { NgSelectComponent, NgOptionComponent, NgLabelTemplateDirective } from '@ng-select/ng-select';
import { MatIcon } from '@angular/material/icon';
import { CustomersVehiclesComponent } from '../../../customers/containers/customers-vehicles/customers-vehicles.component';
import { CustomersVehiclesDetailsNewComponent } from '../../../customers/containers/customers-vehicles-details-new/customers-vehicles-details-new.component';
import { CustomersDetailsNewComponent } from '../../../customers/containers/customers-details-new/customers-details-new.component';
import { CustomersShipToFormComponent } from '../../../customers/components/customers-ship-to-form/customers-ship-to-form.component';
import { VehiclesSelectionComponent } from '../../../vehicles/components/vehicles-selection/vehicles-selection.component';
import { BranchPipe } from '../../pipes/branch.pipe';

@Component({
    selector: 'app-orders-start',
    templateUrl: './orders-start.component.html',
    styleUrls: ['./orders-start.component.scss'],
    imports: [CoreUiModule, FormsModule, ReactiveFormsModule, NgIf, NgSelectComponent, NgFor, NgOptionComponent, MatIcon, NgClass, NgLabelTemplateDirective, CustomersVehiclesComponent, CustomersVehiclesDetailsNewComponent, CustomersDetailsNewComponent, CustomersShipToFormComponent, VehiclesSelectionComponent, AsyncPipe, BranchPipe]
})
export class OrdersStartComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('customerSearch') customerSearch: CustomersSearchComponent;
  @ViewChild('shipToSearch') shipToSearch: CustomersShipToSearchComponent;

  // Form and state variables
  startOrderForm: UntypedFormGroup;
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();
  isAddingVehicle = false;
  isAddingCustomer = false;
  isAddingShipTo = false;
  isOrderReferenceRequired = false;
  hasShownCustomerPrivacyInfo = false;
  isShowReferenceRequired = false;
  formDirty: boolean = false;
  isShipTo: boolean = false;
  formValue = null;
  formValid: boolean = false;
  hasCashDrawer: boolean = false;

  // Observables
  activeOrderVehicle$: Observable<any>;
  loadingCustomerDetails$: Observable<boolean>;
  loadingLookupLists$: Observable<boolean>;
  selectedCustomerVehicleID$: Observable<string>;
  selectedCustomerDetails$: Observable<CustomerDetails>;

  // Data variables
  preSelectedBillToCustomer: Customer;
  selectedShipToCustomer: Customer = null;
  selectedBillToCustomer: Customer;
  selectedCustomerVehicle: OrderCustomerVehicle = null;
  selectedVehicle: {
    customerVehicleID: string;
    partsVehicleID: string;
    vehicleDescription: string;
    jdeVehicleID: string;
  } = null;
  orderTypes: OrderType[];
  nonCorpOrderTypes: OrderType[];
  branchList: Branch[];
  customerShipToList: ShipTos[];
  customerBillToList: CustomerBillTo[];
  customerVehicleList: CustomerVehicle[];
  nonCorpStores: NonCorpStore[] = [];
  allCombinedBranchesList: Branch[] = [];
  filteredStateList: State[];
  newCustomerNumber: string;
  newCustomer: Customer;
  userRole: string;
  defaultCustomer: DefaultCustomer;
  checkedIsGenericQuotesOnlyTF = false;
  preSelectedVehicle = false;
  isSelectingVehicle = false;
  genericCustomer: any;
  shipToCustomerSelection: number = 0;
  shipToCustomerTypeForm: UntypedFormGroup;

  // Constants
  USER_ROLE = USER_ROLE;
  ORDER_TYPE = ORDER_TYPE;

  public lookupLists$ = this.store.select(customersQuery.getLookupLists);

  orderTypeParams: string;
  originalBranchList: Branch[];
  get invalid(): boolean {
    return this.startOrderForm?.invalid;
  }

  private unSubscribe$ = new Subject<void>();

  public get ooeUserState(): IOOEUserState {
    return this.ooeSessionService.getOOEUserState();
  }

  public get isGenericQuotesOnlyTF(): boolean {
    return this.ooeSessionService.isGenericQuotesOnlyTF;
  }

  constructor(
    private store: Store<OrdersPartialState>,
    private coreUIStore: Store<CoreUiPartialState>,
    private customerStore: Store<CustomersPartialState>,
    private apiService: ApiService,
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private cdref: ChangeDetectorRef,
    private ooeSessionService: OOESessionService,
    private customerPrivacyPopup: CustomerPrivacyPopupService
  ) {}

  ngOnInit() {
    this.initRouteParams();
    this.initForms();
    this.dispatchParkedOrdersList();
    this.subscribeSelectedCustomer();
    this.selectedCustomerVehicle = null;
    this.initOrderTypes();
    this.initBrandDefaults();
    this.initLoadingStates();
    this.customerStore.dispatch(new LoadLookupLists());
    this.selectedCustomerVehicleID$ = this.customerStore.select(customersQuery.getCustomerVehicleID);
    this.initCustomerVehicles();
    this.initExternalUserFlow();
    this.initOnBehalfValueChanges();
    this.initOrderTypeValueChanges();
    this.getBranchList();
    this.initFilteredStates();
    this.initShipToNumberValueChanges();
    this.initUserState();
    this.initCustomerNumberValueChanges();
    this.initShipToCustomerSelectionValueChanges();
  }

  private initRouteParams() {
    this.newCustomerNumber = this.route.snapshot.queryParams.customerNumber;
    this.orderTypeParams = this.route.snapshot.queryParams.orderType;
  }

  private initForms() {
    this.startOrderForm = this.formBuilder.group({
      customerNumber: ['', Validators.required],
      customerName: [''],
      shipToNumber: ['', Validators.required],
      orderType: ['', [Validators.required, this.orderTypeValidator()]],
      customerVehicleID: [''],
      reference: [''],
      dispatchFrom: ['', [Validators.required]],
      currencyCode: [''],
      customerTaxRate: [''],
      customerPaymentTerms: [''],
      onBehalf: [ON_BEHALF.branch],
      nonCorpStore: [null],
      shipToName: [''],
      nonCorpStoreSaleYN: ['N'],
    });
    this.shipToCustomerTypeForm = this.formBuilder.group({
      shipToCustomerSelection: [0],
    });
  }

  private subscribeSelectedCustomer() {
    this.store
      .select(customersQuery.getSelectedCustomer)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((customer) => {
        if (customer) {
          this.startOrderForm.patchValue({
            shipToNumber: customer.billToNumber,
            shipToName: customer.billToName,
          });
        }
      });
  }

  private initOrderTypes() {
    combineLatest([
      this.coreUIStore.pipe(select(getOrderTypes)),
      this.coreUIStore.pipe(select(getNonCorpOrderTypes)),
      this.coreUIStore.pipe(select(getCashDrawer)),
    ])
      .pipe(take(1))
      .subscribe(([orderTypes, nonCorpOrderTypes, cashDrawer]) => {
        if (cashDrawer) {
          const register = JSON.parse(cashDrawer);
          this.hasCashDrawer = register && register.status === 'O';
        }
        this.orderTypes = orderTypes.filter((type) => type.PaymentYN !== 'Y' || this.hasCashDrawer);
        this.nonCorpOrderTypes = nonCorpOrderTypes.filter((type) => type.PaymentYN !== 'Y' || this.hasCashDrawer);
        this.startOrderForm.patchValue({
          orderType: this.orderTypes.length ? (this.orderTypeParams ? this.orderTypeParams : this.orderTypes[0].OrderTypeCode) : '',
        });
      });
    this.activeOrderVehicle$ = this.coreUIStore.pipe(select(getActiveOrderVehicle));
  }

  private initBrandDefaults() {
    this.coreUIStore
      .select(getBrandDefaults)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res) => {
        if (res) {
          this.startOrderForm.patchValue({
            currencyCode: res.currrencyCode,
          });
        }
      });
  }

  private initLoadingStates() {
    this.loadingCustomerDetails$ = this.customerStore.pipe(select(customersQuery.getLoadingCustomerDetails));
    this.loadingLookupLists$ = this.customerStore.pipe(select(customersQuery.getLoadingLookupLists));
  }

  private initCustomerVehicles() {
    this.isLoadingSubject.next(true);
    this.startOrderForm.disable();
    this.customerStore
      .select(customersQuery.getCustomerVehicles)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res) => (this.customerVehicleList = res));
  }

  private initExternalUserFlow() {
    combineLatest([this.coreUIStore.pipe(select(getDefaultCustomer)), this.coreUIStore.pipe(select(getUserRole))])
      .pipe(
        filter(([, userRole]) => !!userRole && !this.defaultCustomer),
        debounceTime(500),
        switchMap(([customer, userRole]) => {
          this.userRole = userRole;
          this.defaultCustomer = customer;
          this.getNonCorpStores();
          if (this.newCustomerNumber && !this.isGenericQuotesOnlyTF) {
            this.apiService
              .fetchCustomerDetails({ customerNumber: this.newCustomerNumber })
              .pipe(takeUntil(this.unSubscribe$))
              .subscribe((dataDetails) => {
                if (dataDetails?.ErrorFlag === '0') {
                  const customer = dataDetails.SearchResults[0];
                  this.selectShipToCustomer(customer);
                  this.coreUIStore.dispatch(clearSearchData());
                }
              });
          }
          if (this.isExternalOrProgrammeUser()) {
            return this.apiService.fetchCustomerDetails({
              customerNumber: this.ooeUserState?.externalUsersUsualCustomerNumber,
            });
          }
          return of(null);
        }),
        switchMap((x) => {
          if (x?.SearchResults?.length) {
            this.preSelectedBillToCustomer = x.SearchResults[0];
            this.startOrderForm.patchValue({
              shipToNumber: this.preSelectedBillToCustomer.customerNumber,
              shipToName: this.preSelectedBillToCustomer.customerName,
              currencyCode: this.preSelectedBillToCustomer.currencyCode,
              customerTaxRate: this.preSelectedBillToCustomer.customerTaxRate,
              customerPaymentTerms: this.preSelectedBillToCustomer.customerPaymentTerms,
              dispatchFrom: this.getPreselectedBranch(),
            });
            this.allCombinedBranchesList = getCombinedBranches(
              this.preSelectedBillToCustomer?.allowedBranches,
              this.preSelectedBillToCustomer?.allowedNonCorps
            );
            this.customerShipToList = x.SearchResults[0].shipTos;
            this.startOrderForm.patchValue(
              {
                shipToName: this.customerShipToList?.find((x) => x.shipToNumber == this.startOrderForm.controls.shipToNumber.value)?.shipToName || '',
              },
              { emitEvent: false }
            );
          }
          return of(null);
        }),
        takeUntil(this.unSubscribe$)
      )
      .subscribe(() => {
        this.isLoadingSubject.next(false);
        this.startOrderForm.enable();
      });
  }

  private initOnBehalfValueChanges() {
    this.startOrderForm.controls.onBehalf.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value: string) => {
      const orderType = this.startOrderForm.controls.orderType.value;
      if (value === ON_BEHALF.branch) {
        this.startOrderForm.controls.dispatchFrom.setValidators([Validators.required]);
        this.startOrderForm.controls.nonCorpStore.clearValidators();
        this.startOrderForm.controls.orderType.setErrors(null);
        if (orderType === ORDER_TYPE.showSaleOrder) {
          this.startOrderForm.patchValue({ orderType: ORDER_TYPE.saleOrder });
        }
      } else {
        this.startOrderForm.controls.nonCorpStore.setValidators([Validators.required]);
        this.startOrderForm.controls.dispatchFrom.clearValidators();
        if (orderType === ORDER_TYPE.saleOrder) {
          this.startOrderForm.patchValue({ orderType: ORDER_TYPE.showSaleOrder });
        } else if (this.nonCorpOrderTypes.findIndex((item) => item.OrderTypeCode === orderType) === -1) {
          this.startOrderForm.controls.orderType.setErrors({ invalidNonCorp: true });
        }
      }
      this.startOrderForm.controls.dispatchFrom.updateValueAndValidity();
      this.startOrderForm.controls.nonCorpStore.updateValueAndValidity();
    });
  }

  private initOrderTypeValueChanges() {
    this.startOrderForm.controls.orderType.valueChanges.pipe(takeUntil(this.unSubscribe$), distinctUntilChanged()).subscribe((value: string) => {
      if (!this.checkedIsGenericQuotesOnlyTF && this.isGenericQuotesOnlyTF) {
        this.apiService
          .fetchGenericCustomer()
          .pipe(takeUntil(this.unSubscribe$))
          .subscribe((data) => {
            if (data?.ErrorFlag === '0') {
              this.genericCustomer = data;
            }
            const defaultShipTo = this.ooeSessionService.getOOEUserState().defaultShipTo;
            if (!defaultShipTo) {
              this.toastr.error('Default Ship To is missing.');
              return;
            }
            this.apiService
              .fetchCustomerDetails({ customerNumber: defaultShipTo })
              .pipe(takeUntil(this.unSubscribe$))
              .subscribe((dataDetails) => {
                if (dataDetails?.ErrorFlag === '0') {
                  const customer = dataDetails.SearchResults[0];
                  this.selectShipToCustomer(customer, data?.customerName, data?.customerNumber);
                  this.coreUIStore.dispatch(clearSearchData());
                  this.checkedIsGenericQuotesOnlyTF = true;
                }
              });
          });
      }
      this.isOrderReferenceRequired = value === ORDER_TYPE.arbOrder;
      if (this.isExternalOrProgrammeUser()) {
        if (value === ORDER_TYPE.arbOrder) {
          if (this.customerShipToList?.length) {
            this.startOrderForm.patchValue(
              {
                customerNumber: this.startOrderForm.controls.shipToNumber.value,
                shipToNumber: this.ooeUserState.defaultShipTo.toString(),
                shipToName: this.customerShipToList?.find((x) => x.shipToNumber == this.ooeUserState.defaultShipTo.toString())?.shipToName || '',
              },
              { emitEvent: false }
            );
          }
        } else if (this.isGenericQuotesOnlyTF) {
          this.startOrderForm.patchValue(
            {
              customerNumber: this.genericCustomer?.customerNumber,
              customerName: this.genericCustomer?.customerName,
              shipToNumber: this.ooeUserState.defaultShipTo.toString(),
              shipToName: this.genericCustomer?.customerName,
            },
            { emitEvent: false }
          );
        }
      }
    });
  }

  private initFilteredStates() {
    this.store
      .select(customersQuery.getFilteredStates)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((filteredStates) => {
        setTimeout(() => {
          this.filteredStateList = filteredStates;
        }, 0);
      });
  }

  private initShipToNumberValueChanges() {
    this.startOrderForm.controls.shipToNumber.valueChanges.pipe(takeUntil(this.unSubscribe$), distinctUntilChanged()).subscribe((value: string) => {
      if (value) {
        const shipToName = this.customerShipToList?.find((x) => x.shipToNumber == value.toString())?.shipToName || '';
        if (this.startOrderForm.controls.shipToName.value !== shipToName) {
          this.startOrderForm.patchValue(
            {
              shipToNumber: value.toString(),
              shipToName,
            },
            { emitEvent: false }
          );
        }
      }
    });
  }

  private initUserState() {
    const userState = this.ooeSessionService.getOOEUserState();
    this.preSelectedVehicle = !!userState?.jdeVehicleID;
  }
//this.preSelectedVehicle = !!userState.jdeVehicleID;
  private initCustomerNumberValueChanges() {
    this.startOrderForm.controls.customerNumber.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value: string) => {
      if (value && this.selectedShipToCustomer?.customerType === CUSTOMER_TYPE.billTo) {
        this.startOrderForm.patchValue(
          {
            customerNumber: value.toString(),
            customerName: this.customerBillToList?.find((x) => x.billToNumber == value.toString())?.billToName,
          },
          { emitEvent: false }
        );
      }
    });
  }

  private initShipToCustomerSelectionValueChanges() {
    this.shipToCustomerTypeForm.controls.shipToCustomerSelection.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((value: number) => {
      this.shipToCustomerSelection = value;
      if (value === 1) {
        this.startOrderForm.patchValue(
          {
            shipToNumber: '',
            shipToName: '',
          },
          { emitEvent: false }
        );
        this.startOrderForm.markAsDirty();
      }
      if (value === 0) {
        this.startOrderForm.patchValue(
          {
            shipToNumber: this.customerShipToList[0]?.shipToNumber,
            shipToName: this.customerShipToList[0]?.shipToName || '',
          },
          { emitEvent: false }
        );
      }
    });
  }

  ngAfterViewInit() {
    this.cdref.detectChanges();
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  getNonCorpStores(): void {
    if (this.userRole === USER_ROLE.showExternal || this.userRole === USER_ROLE.showInternal) {
      this.apiService
        .getNonCorpStoresForShow()
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((storeData) => {
          this.nonCorpStores = storeData.SearchResults || [];
        });
    }
  }

  getBranchList(): void {
    this.apiService
      .getBranchList()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((branchData) => {
        this.branchList = branchData.SearchResults;
        this.originalBranchList = branchData.SearchResults;
      });
  }

  selectShipToCustomer(customer: Customer, customerName: string = '', customerNumber: string = ''): void {
    this.coreUIStore.dispatch(clearSearchData());
    this.isShipTo = false;
    this.selectedCustomerVehicle = null;
    this.selectedShipToCustomer = customer;
    this.startOrderForm.patchValue({ customerNumber: '' });

    if (customer) {
      this.newCustomerNumber = null;

      const isExternal = this.isExternalOrProgrammeUser();
      const isGeneric = this.isGenericQuotesOnlyTF;
      if (this.startOrderForm.controls.orderType.value === ORDER_TYPE.arbOrder && isExternal) {
        this.startOrderForm.patchValue(
          {
            //branch: customer.branchCode,
            currencyCode: customer.currencyCode,
            customerPaymentTerms: customer.customerPaymentTerms,
            customerTaxRate: customer.customerTaxRate,
            shipToNumber: customer.customerNumber,
            shipToName: customer.customerName,
          },
          { emitEvent: false }
        );
      } else {
        this.startOrderForm.patchValue(
          {
            shipToNumber: isExternal ? this.startOrderForm.controls.shipToNumber?.value : customer.customerNumber,
            shipToName: isGeneric ? customerName : customer.customerName,
            //branch: customer.branchCode,
            currencyCode: customer.currencyCode,
            customerPaymentTerms: customer.customerPaymentTerms,
            customerTaxRate: customer.customerTaxRate,
          },
          { emitEvent: false }
        );
        if (!isExternal) {
          this.startOrderForm.patchValue(
            {
              dispatchFrom: customer.branchCode,
            },
            { emitEvent: false }
          );
        }
        if (customer.customerType === CUSTOMER_TYPE.billTo && !this.isGenericQuotesOnlyTF && !isExternal) {
          //set default branch from shipTos defaultBranch value
          this.allCombinedBranchesList = getCombinedBranches(customer?.allowedBranches, customer?.allowedNonCorps);
          //concat allCombinedBranchesList to branchList
          this.branchList = [
            ...this.branchList,
            ...this.allCombinedBranchesList.filter((b) => !this.branchList.some((x) => x.BranchCode === b.BranchCode)),
          ];
          const branch =
            customer.shipTos?.length > 0
              ? customer.shipTos.filter((shipTo) => shipTo.shipToNumber === customer.customerNumber)?.[0]?.defaultBranch
              : null;
          this.startOrderForm.patchValue(
            {
              dispatchFrom: branch || customer.branchCode,
            },
            { emitEvent: false }
          );
        } else {
          this.branchList = this.originalBranchList;
        }
      }

      if (customer.customerType === CUSTOMER_TYPE.shipTo && !this.isGenericQuotesOnlyTF) {
        this.isShipTo = true;
        this.selectedBillToCustomer = null;
        this.apiService
          .fetchCustomerBillTo({ customerShipTo: customer.customerNumber })
          .pipe(takeUntil(this.unSubscribe$))
          .subscribe((result) => {
            if (result?.SearchResults?.length) {
              this.customerBillToList = result.SearchResults;
              this.startOrderForm.patchValue({
                customerNumber: this.customerBillToList[0].billToNumber,
              });
            }
          });
      } else if (customer.customerType === CUSTOMER_TYPE.billTo && !this.isGenericQuotesOnlyTF) {
        this.customerShipToList = customer.shipTos?.map((shipTo: any) => ({
          shipToNumber: shipTo.shipToNumber,
          shipToName: shipTo.shipToName,
          defaultBranch: shipTo.defaultBranch ?? '',
          shipToAddressLine1: shipTo?.shipToAddressLine1 || '',
        })) || [
          {
            shipToNumber: customer?.customerNumber,
            shipToName: customer?.customerName,
            defaultBranch: customer?.branchCode ?? '',
            shipToAddressLine1: '',
          },
        ];
        this.startOrderForm.patchValue(
          {
            shipToNumber: this.customerShipToList[0]?.shipToNumber,
            shipToName: this.customerShipToList[0]?.shipToName || '',
            customerNumber: customer?.customerNumber,
          },
          { emitEvent: false }
        );
      } else {
        if (customer.customerType === CUSTOMER_TYPE.retail) {
          this.coreUIStore.pipe(select(getBranch), take(1), takeUntil(this.unSubscribe$)).subscribe((branch) => {
            if (branch) {
              this.startOrderForm.patchValue({ dispatchFrom: branch.BranchCode });
            }
          });
        }
        this.startOrderForm.patchValue({
          customerNumber: isGeneric ? customerNumber : customer.customerNumber,
        });
      }
    }

    const selectedCustomerNumber = this.startOrderForm.controls.customerNumber.value;
    if (selectedCustomerNumber && !this.isGenericQuotesOnlyTF) {
      this.store.dispatch(
        new LoadCustomerVehicles({
          customerNumber: selectedCustomerNumber,
          retiredVehicleYN: 'Y',
        })
      );
      this.selectedCustomerVehicle = {
        partsVehicleId: customer?.partsVehicleID?.toString(),
        customerVehicleId: customer?.customerVehicleID?.toString(),
        vehicleShortDescription: customer?.vehicleDescription,
      };
    }
  }

  selectOneOffCustomer(customer) {
    if (customer) {
      this.startOrderForm.patchValue(
        {
          shipToNumber: customer.customerNumber,
          shipToName: customer.customerName,
        },
        { emitEvent: false }
      );
    } else {
      this.startOrderForm.patchValue(
        {
          shipToNumber: '',
          shipToName: '',
        },
        { emitEvent: false }
      );
    }
  }

  ////////////////////////////////////////////
  selectBillToCustomer(e: any) {
    this.selectedBillToCustomer = e;
  }

  selectCustomerVehicle(v: CustomerVehicle) {
    this.selectedVehicle = null;
    this.selectedCustomerVehicle = {
      partsVehicleId: v.partsVehicleID,
      customerVehicleId: v.customerVehicleID,
      vehicleShortDescription: v.ShortDescription,
    };
    this.modalService.dismissAll();
  }

  getSelectedVehicle(v: any) {
    this.selectedVehicle = v;
    if (!this.selectedVehicle?.customerVehicleID || this.selectedVehicle?.vehicleDescription === '') {
      if (this.ooeUserState?.OpenOrder?.vehicleDescription) {
        this.selectedVehicle = {
          customerVehicleID: this.ooeUserState?.OpenOrder?.customerVehicleID,
          partsVehicleID: this.ooeUserState?.OpenOrder?.partsVehicleID.toString(),
          vehicleDescription: this.ooeUserState?.OpenOrder?.vehicleDescription,
          jdeVehicleID: this.ooeUserState?.jdeVehicleID.toString(),
        };
      }
      this.isAddingVehicle = true;
      this.isSelectingVehicle = false;
      return true;
    }
    this.selectedCustomerVehicle = null;
    return false;
  }

  closeAddingVehicle(customerVehicleID = null) {
    this.modalService.dismissAll();
    this.isAddingVehicle = false;
    if (customerVehicleID) {
      this.selectedCustomerVehicle = null;
      const newCusomterVehicle = this.customerVehicleList?.find((x) => x.customerVehicleID === customerVehicleID);
      if (newCusomterVehicle) {
        this.selectedVehicle['vehicleDescription'] = newCusomterVehicle?.ShortDescription;
      }
      this.selectedVehicle = Object.assign([], this.selectedVehicle);
      this.selectedVehicle['customerVehicleID'] = customerVehicleID;
      const newSelectedVehicle = {
        customerVehicleID: this.selectedVehicle.customerVehicleID,
        partsVehicleID: this.selectedVehicle.partsVehicleID,
        vehicleDescription: this.selectedVehicle.vehicleDescription,
        jdeVehicleID: this.selectedVehicle.jdeVehicleID,
      };
      this.apiService
        .updateSelectedVehicle(newSelectedVehicle)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((res) => {
          if (res) {
            this.coreUIStore.dispatch(setSelectedVehicle(newSelectedVehicle));
            this.messageService.dispatchAction(setSelectedVehicle(newSelectedVehicle));
          }
        });
    } else {
      this.selectedVehicle = null;
    }
  }

  open(content: any) {
    if (this.selectedShipToCustomer?.genericVehiclesOnlyTF && !this.isAddingVehicle) {
      this.isSelectingVehicle = true;
    }
    this.modalService.open(content, {
      windowClass: 'garage-modal',
      size: 'xl',
    });
  }

  vehicleDescription() {
    return this.selectedCustomerVehicle
      ? this.selectedCustomerVehicle.vehicleShortDescription
      : this.selectedVehicle
      ? this.selectedVehicle.vehicleDescription
      : '';
  }

  createOrderConfirm(toOrder = false) {
    this.showCustomerPrivacyPolicy().subscribe((val) => {
      if (val) {
        this.createOrder(toOrder);
      }
    });
  }

  createOrder(toOrder = false) {
    const orderType = this.startOrderForm.controls.orderType.value;
    let customerName = this.isGenericQuotesOnlyTF ? this.startOrderForm?.controls.shipToName.value : this.selectedShipToCustomer?.customerName;
    // Patch vehicle info
    if (this.selectedVehicle) {
      this.startOrderForm.patchValue({
        customerVehicleID: this.selectedVehicle.customerVehicleID,
      });
    } else if (this.selectedCustomerVehicle?.customerVehicleId) {
      this.startOrderForm.patchValue({
        partsVehicleID: this.selectedCustomerVehicle.partsVehicleId,
        customerVehicleID: this.selectedCustomerVehicle.customerVehicleId,
        vehicleShortDescription: this.selectedCustomerVehicle.vehicleShortDescription,
      });
    }

    // Patch non-corp store branch
    if (this.startOrderForm.controls.onBehalf.value === ON_BEHALF.nonCorp) {
      this.startOrderForm.patchValue({
        dispatchFrom: this.startOrderForm.controls.nonCorpStore.value?.customerNumber?.toString(),
      });
    } else if (this.selectedShipToCustomer?.customerType === CUSTOMER_TYPE.shipTo && orderType !== ORDER_TYPE.arbOrder) {
      this.startOrderForm.patchValue({
        shipToNumber: this.selectedShipToCustomer?.customerNumber,
        shipToName: this.selectedShipToCustomer?.customerName,
      });
    }

    const data = {
      ...this.startOrderForm.getRawValue(),
      customerName,
      quickSaleYN: 'N',
      // dispatchFrom: this.startOrderForm.controls.dispatchFrom.value?.toString(),
    };

    // Remove unused fields
    delete data.nonCorpStore;
    delete data.nonCorpStoreSaleYN;
    delete data.onBehalf;
    //delete data.branch;

    // Dispatch order based on type
    const dispatchOrder = (payload) => {
      this.store.dispatch(
        new CreateOrder({
          ...payload,
          isNewOrder: this.isGenericQuotesOnlyTF,
        })
      );
    };

    switch (orderType) {
      case ORDER_TYPE.quoteOrder: {
        if (this.isExternalOrProgrammeUser()) {
          Object.assign(data, {
            shipToNumber: this.selectedShipToCustomer?.customerNumber,
            shipToName: this.selectedShipToCustomer?.customerName,
            reference: '',
            dispatchFrom: this.isGenericQuotesOnlyTF ? this.selectedShipToCustomer?.customerNumber : this.ooeUserState?.defaultShipTo?.toString(),
          });
        } else {
          Object.assign(data, {
            shipToNumber: this.selectedShipToCustomer?.customerNumber,
            shipToName: this.selectedShipToCustomer?.customerName,
            reference: '',
            dispatchFrom: this.startOrderForm?.controls.dispatchFrom.value?.toString(),
          });
        }
        dispatchOrder({ data, toOrder });
        break;
      }
      case ORDER_TYPE.arbOrder: {
        if (this.isExternalOrProgrammeUser()) {
          const branchValue = this.startOrderForm.controls.dispatchFrom?.value?.toString();
          const isBranch = this.branchList.some((el) => el.BranchCode === branchValue);
          const isCombinedBranch = this.allCombinedBranchesList.some((el) => el.BranchCode === branchValue);
          customerName = this.startOrderForm.controls.shipToName.value || this.selectedShipToCustomer?.customerName;
          if (isBranch || isCombinedBranch) {
            const customerNumber = isCombinedBranch
              ? this.ooeUserState?.externalUsersUsualCustomerNumber
              : this.preSelectedBillToCustomer?.customerNumber;
            const customerName = this.preSelectedBillToCustomer?.customerName;

            Object.assign(data, {
              customerNumber,
              customerName,
              shipToName: this.startOrderForm.controls.shipToName.value,
              shipToNumber: this.startOrderForm.controls.shipToNumber.value,
              dispatchFrom: branchValue,
            });
          }
        }
        dispatchOrder({ data, toOrder });
        break;
      }
      case ORDER_TYPE.saleOrder: {
        if (this.isExternalOrProgrammeUser()) {
          Object.assign(data, {
            shipToNumber: this.selectedShipToCustomer?.customerNumber,
            shipToName: this.selectedShipToCustomer?.customerName,
          });
        }
        dispatchOrder({ data, toOrder });
        break;
      }
      case ORDER_TYPE.showSaleOrder: {
        dispatchOrder({ data, toOrder });
        break;
      }
      default: {
        dispatchOrder({ data, toOrder });
        break;
      }
    }
  }

  clearVehicle() {
    this.selectedCustomerVehicle = null;
    this.selectedVehicle = {
      customerVehicleID: '',
      partsVehicleID: '',
      vehicleDescription: '',
      jdeVehicleID: '',
    };

    if (this.ooeUserState?.OpenOrder?.vehicleDescription.trim() !== '') {
      this.preSelectedVehicle = true;
    }
  }

  cancel() {
    window.history.back();
  }

  addNewCustomer() {
    this.isAddingCustomer = true;
    return true;
  }

  addingNewShipToCustomer() {
    this.selectedCustomerDetails$ = of({
      customerName: this.preSelectedBillToCustomer?.customerName || this.startOrderForm?.controls?.customerName?.value,
      customerNumber: this.preSelectedBillToCustomer?.customerNumber || this.startOrderForm?.controls?.shipToNumber?.value,
    } as CustomerDetails);
    this.isAddingShipTo = true;
    return true;
  }
  getFilteredStateList(countryCode) {
    this.store.dispatch(new SelectCountry(countryCode));
  }

  handleNewCustomer(customerNumber: string) {
    this.modalService.dismissAll();
    this.isAddingCustomer = false;

    if (customerNumber) {
      this.newCustomerNumber = customerNumber;
      this.isLoadingSubject.next(true);
      this.apiService
        .customerSearch({
          customerfuzzySearch: '',
          customerNumber: +this.newCustomerNumber,
          customerType: '',
        })
        .pipe(
          takeUntil(this.unSubscribe$),
          finalize(() => this.isLoadingSubject.next(false))
        )
        .subscribe((res) => {
          this.newCustomer = res.SearchResults.find((customer) => +customer.customerNumber === +this.newCustomerNumber) || res.SearchResults[0];
          this.customerSearch.selectCustomer(this.newCustomer);
        });
    }
  }

  getCustomerName(): string {
    if (this.selectedShipToCustomer) {
      return this.selectedShipToCustomer.customerName + ' (' + this.selectedShipToCustomer.customerNumber + ')';
    } else if (this.isAddingCustomer && this.newCustomer) {
      return this.newCustomer.customerName + ' (' + this.newCustomer.customerNumber + ')';
    } else {
      return '';
    }
  }

  orderTypeValidator(): ValidatorFn {
    return (orderType: UntypedFormControl): ValidationErrors => {
      let errors = null;
      if ((orderType.value === ORDER_TYPE.creditPrice || orderType.value === ORDER_TYPE.creditReturn) && !this.hasCashDrawer) {
        errors = { invalidOrderType: true };
      }
      return errors;
    };
  }

  //Generate Reference field error message toastr
  createReferenceFieldErrorMessage(key: string) {
    if (key === 'reference') {
      if (this.isValidatorExist(key)) {
        this.startOrderForm
          .get(key)
          .valueChanges.pipe(takeUntil(this.unSubscribe$))
          .subscribe((val) => {
            if (val.trim() === '' && this.startOrderForm.controls.orderType.value !== ORDER_TYPE.quoteOrder) {
              this.startOrderForm.controls.reference.setErrors({
                invalid: true,
              });
              //this.toastr.error('Reference field cannot be empty, please provide value.');
            } else {
              this.startOrderForm.controls.reference.setErrors(null);
            }
          });
      }
    }
  }

  //Check if validation exist or not
  isValidatorExist(field: string) {
    const form_field = this.startOrderForm.get(field);
    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return validator && validator.required;
  }

  dispatchParkedOrdersList() {
    this.store.dispatch(new LoadParkedOrdersList());
  }

  handleCloseModal() {
    this.modalService.dismissAll();
  }
  saveShipTo() {
    this.apiService
      .createShipToCustomer(this.formValue)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((result) => {
        if (result.ErrorFlag === '0') {
          this.modalService.dismissAll();
          this.toastr.success('Ship To Customer created successfully');
          this.startOrderForm.patchValue({
            shipToNumber: result.CustomerNumber,
            shipToName: this.formValue.name,
          });
          this.apiService
            .fetchCustomerDetails({
              customerNumber: this.startOrderForm.controls.customerNumber.value,
            })
            .pipe(takeUntil(this.unSubscribe$))
            .subscribe((dataDetails) => {
              if (dataDetails?.ErrorFlag === '0') {
                const customer = dataDetails.SearchResults[0];
                this.selectShipToCustomer(customer);
              }
            });
        }
      });
  }

  isExternalOrProgrammeUser(): boolean {
    return [USER_ROLE.external, USER_ROLE.programme].includes(this.userRole);
  }
  updateHasShownCustomerPrivacyInfo() {
    this.hasShownCustomerPrivacyInfo = true;
  }

  showCustomerPrivacyPolicy() {
    if (this.isExternalOrProgrammeUser() && this.startOrderForm.controls.orderType.value === ORDER_TYPE.quoteOrder && !this.isGenericQuotesOnlyTF) {
      return this.customerPrivacyPopup.confirm().pipe(
        switchMap((val) => {
          if (!!val) {
            return of(true);
          }
          return of(false);
        })
      );
    } else {
      return of(true);
    }
  }

  formDirtyHandler(dirty) {
    this.formDirty = dirty;
    if (dirty) {
      this.store.dispatch(hasUnsavedChanges({ unsavedChanges: true }));
      this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: true }));
    }
  }

  onSelectVehicle(vehicle: Vehicle) {
    this.selectedVehicle = {
      customerVehicleID: vehicle.VehicleID,
      partsVehicleID: vehicle.partsVehicleID,
      vehicleDescription: `${vehicle.Make} ${vehicle.Model} ${vehicle.Series}`,
      jdeVehicleID: vehicle.VehicleID,
    };
    this.isSelectingVehicle = false;
    this.isAddingVehicle = true;
  }

  onShipToNumberChange(customer: Customer) {
    if (customer) {
    }
  }

  getPreselectedBranch(): string {
    return this.isExternalOrProgrammeUser()
      ? this.preSelectedBillToCustomer?.shipTos?.find((x) => Number(x.shipToNumber) === Number(this.ooeUserState.defaultShipTo))?.defaultBranch
      : this.preSelectedBillToCustomer.branchCode;
  }
}
